import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowForward,
  Memory,
  Cloud,
  Security,
  Speed,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Retro VHS Scanlines Effect
const Scanlines = () => (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 9999,
      opacity: 0.03,
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
    }}
  />
);

// Retro Grid Background
const RetroGrid = () => (
  <Box
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '50vh',
      background: `
        linear-gradient(to bottom, transparent 0%, #0D0D0D 100%),
        linear-gradient(90deg, rgba(255, 107, 26, 0.1) 1px, transparent 1px),
        linear-gradient(rgba(255, 107, 26, 0.1) 1px, transparent 1px)
      `,
      backgroundSize: '100% 100%, 40px 40px, 40px 40px',
      transform: 'perspective(500px) rotateX(60deg)',
      transformOrigin: 'bottom center',
      pointerEvents: 'none',
      opacity: 0.4,
    }}
  />
);

// Comparison Item
const ComparisonItem = ({ ours, theirs, label }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      gap: 3,
      alignItems: 'center',
      py: 3,
      borderBottom: '1px solid #2A2A2A',
    }}
  >
    <Box sx={{ textAlign: 'right' }}>
      <Typography
        sx={{
          color: '#FF6B1A',
          fontWeight: 600,
          fontSize: '1rem',
        }}
      >
        {ours}
      </Typography>
    </Box>
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: '#1A1A1A',
        border: '2px solid #2A2A2A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Space Mono", monospace',
          color: '#666',
          fontSize: '0.65rem',
          fontWeight: 700,
        }}
      >
        VS
      </Typography>
    </Box>
    <Box>
      <Typography
        sx={{
          color: '#666',
          fontSize: '1rem',
        }}
      >
        {theirs}
      </Typography>
    </Box>
  </Box>
);

// Pillar Card
const PillarCard = ({ icon, title, description }) => (
  <Box
    sx={{
      p: 4,
      backgroundColor: '#151515',
      border: '2px solid #2A2A2A',
      height: '100%',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#FF6B1A',
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
        '& svg': {
          fontSize: 32,
          color: '#FF6B1A',
        },
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="h5"
      sx={{
        mb: 2,
        fontFamily: '"Bebas Neue", sans-serif',
        letterSpacing: '0.05em',
        fontSize: '1.5rem',
        color: '#F5F0E6',
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        color: '#888888',
        lineHeight: 1.8,
      }}
    >
      {description}
    </Typography>
  </Box>
);

const AboutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const comparisons = [
    { ours: 'Dedicated Hardware', theirs: 'Manual Setup / Laptop', label: 'Connection' },
    { ours: 'Auto-failover', theirs: 'No Backup Plan', label: 'Reliability' },
    { ours: 'US-based support', theirs: 'Unresponsive Agencies', label: 'Support' },
    { ours: '10% flat fee', theirs: 'High Fees & Commissions', label: 'Cost' },
    { ours: 'Enterprise-grade audio', theirs: 'Inconsistent Quality', label: 'Quality' },
  ];

  const pillars = [
    {
      icon: <Memory />,
      title: 'The SetSync Box',
      description:
        'We deploy the dedicated SetSync Box to venues. No laptops. No tablets. No browser tabs to accidentally close.',
    },
    {
      icon: <Cloud />,
      title: 'Infrastructure Mindset',
      description:
        'SetSync is not a website. We are utility infrastructure — as essential to a venue as electricity or WiFi.',
    },
    {
      icon: <Speed />,
      title: 'Global Network',
      description:
        'Our low-latency network ensures your drops land exactly when they should, anywhere in the world.',
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: '#0D0D0D',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Scanlines />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 16, md: 24 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
        }}
      >
        <RetroGrid />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: '#FF6B1A',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              About SetSync
            </Typography>

            <Typography
              variant="h1"
              sx={{
                color: '#F5F0E6',
                mb: 4,
                fontSize: { xs: '2.5rem', md: '4rem' },
                lineHeight: 1.1,
              }}
            >
              We Are Not a Website.
              <br />
              <Box component="span" sx={{ color: '#FF6B1A' }}>
                We Are Infrastructure.
              </Box>
            </Typography>

            <Typography
              sx={{
                color: '#888888',
                fontFamily: '"Space Mono", monospace',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
                fontSize: { xs: '0.9rem', md: '1rem' },
              }}
            >
              SetSync turns every venue sound system into a global stage by
              deploying the dedicated SetSync Box that streams live DJ
              performances.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          backgroundColor: '#0A0A0A',
          borderTop: '1px solid #1A1A1A',
          borderBottom: '1px solid #1A1A1A',
        }}
      >
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: '#FF6B1A',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: '#F5F0E6',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  mb: 3,
                }}
              >
                Replace Middlemen with Middleware
              </Typography>
              <Typography
                sx={{
                  color: '#888',
                  lineHeight: 1.8,
                }}
              >
                Traditional booking agencies take 20-30% cuts while providing
                inconsistent quality. We built SetSync to eliminate the
                friction between DJs and venues by creating reliable
                infrastructure that just works.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  border: '2px solid #FF6B1A',
                  background: 'linear-gradient(135deg, rgba(255, 107, 26, 0.1) 0%, rgba(255, 107, 26, 0.05) 100%)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Bebas Neue", sans-serif',
                    fontSize: '1.5rem',
                    color: '#FF6B1A',
                    mb: 2,
                    letterSpacing: '0.05em',
                  }}
                >
                  "The utility provider for nightlife entertainment"
                </Typography>
                <Typography
                  sx={{
                    color: '#888',
                    fontFamily: '"Space Mono", monospace',
                    fontSize: '0.85rem',
                  }}
                >
                  — Our vision for SetSync
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* The Difference Section */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: '#FF6B1A',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              The Difference
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: '#F5F0E6',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              SetSync vs The Legacy Model
            </Typography>
          </Box>

          <Box
            sx={{
              p: 4,
              backgroundColor: '#151515',
              border: '2px solid #2A2A2A',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                gap: 3,
                pb: 2,
                borderBottom: '2px solid #2A2A2A',
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  textAlign: 'right',
                  fontFamily: '"Bebas Neue", sans-serif',
                  color: '#FF6B1A',
                  fontSize: '1.25rem',
                  letterSpacing: '0.05em',
                }}
              >
                SetSync
              </Typography>
              <Box />
              <Typography
                sx={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  color: '#666',
                  fontSize: '1.25rem',
                  letterSpacing: '0.05em',
                }}
              >
                Legacy Model
              </Typography>
            </Box>

            {comparisons.map((item, index) => (
              <ComparisonItem key={index} {...item} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Pillars Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          backgroundColor: '#0A0A0A',
          borderTop: '1px solid #1A1A1A',
          borderBottom: '1px solid #1A1A1A',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: '#FF6B1A',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Our Pillars
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: '#F5F0E6',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              What We Believe
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {pillars.map((pillar, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <PillarCard {...pillar} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Why We Built This */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: '#FF6B1A',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              The Story
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: '#F5F0E6',
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 4,
              }}
            >
              Why We Built This
            </Typography>
          </Box>

          <Box
            sx={{
              p: { xs: 4, md: 6 },
              backgroundColor: '#151515',
              border: '2px solid #2A2A2A',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 4,
                height: '100%',
                backgroundColor: '#FF6B1A',
              },
            }}
          >
            <Typography
              sx={{
                color: '#CCCCCC',
                lineHeight: 2,
                fontSize: '1.1rem',
                mb: 3,
              }}
            >
              We've seen too many DJs stuck in a broken system — fighting for
              local gigs, dealing with unreliable agency middlemen, and getting
              paid less than they deserve.
            </Typography>
            <Typography
              sx={{
                color: '#CCCCCC',
                lineHeight: 2,
                fontSize: '1.1rem',
                mb: 3,
              }}
            >
              We've seen too many venues struggling with inconsistent
              entertainment — different DJs every week, complicated booking
              processes, and technology that fails when it matters most.
            </Typography>
            <Typography
              sx={{
                color: '#FF6B1A',
                lineHeight: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              SetSync fixes both problems with a single piece of hardware and a
              transparent credit system.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 12, md: 20 },
          backgroundColor: '#0A0A0A',
          borderTop: '1px solid #1A1A1A',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              border: '2px solid #2A2A2A',
              backgroundColor: 'rgba(13, 13, 13, 0.9)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: '10%',
                right: '10%',
                height: 4,
                backgroundColor: '#FF6B1A',
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: '#F5F0E6',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Ready to Join Us?
            </Typography>
            <Typography
              sx={{
                color: '#888888',
                fontFamily: '"Space Mono", monospace',
                mb: 5,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Whether you're a venue looking for reliable entertainment or a DJ
              ready to expand your reach — we're building this for you.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/venues"
                endIcon={<ArrowForward />}
                sx={{ px: 4, py: 1.5 }}
              >
                I'm a Venue
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/djs"
                sx={{ px: 4, py: 1.5 }}
              >
                I'm a DJ
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;

